/**
 * Geode Voice (UPT-2000 lot V0) — magasin zustand du canal vocal : état,
 * transcriptions, cartes, rapports, erreurs. Mémoire seule (jamais de
 * `localStorage` : l'éphémère ne quitte pas la session).
 */
import { create } from 'zustand';
import type {
  VoiceCard,
  VoiceLimits,
  VoiceReport,
  VoiceSource,
  VoiceState,
  VoiceTranscript,
  VoiceUsage,
} from './voiceTypes';

/** Valeurs d'interpolation d'un message d'erreur (ex. `{minutes}` du silence). */
export type VoiceErrorValues = Record<string, string | number>;

/** Lecture de la consommation du mois (`GET …/voice/usage`) : un seul appel par ouverture et par arrêt. */
export type VoiceUsageStatus = 'idle' | 'loading' | 'ready' | 'error';

export type VoiceStoreState = {
  state: VoiceState;
  error: string | null;
  /** Clé i18n de l'erreur courante (rendue traduite). */
  errorId: string | null;
  /** Valeurs du message d'erreur courant (ex. `{ minutes }`). */
  errorValues: VoiceErrorValues | null;
  transcripts: VoiceTranscript[];
  cards: VoiceCard[];
  reports: VoiceReport[];
  /** Simulateur actif pour la session (drapeau local ou bascule proposée). */
  mockActive: boolean;
  /** Niveau audio du micro (0..1), indicateur sobre du bouton. */
  level: number;
  /** Une session RÉELLE s'est ouverte sur cette page (la ligne de consommation ne vit que pour elle). */
  realSession: boolean;
  /** Origine de la clé de la dernière session réelle (le serveur décide, le web affiche). */
  source: VoiceSource | null;
  /** Limites de la dernière session réelle. */
  limits: VoiceLimits | null;
  /** Durée de la session réelle en cours (ou de la dernière), en secondes. */
  sessionSeconds: number;
  /** Dernière consommation du mois connue (acquittement d'un rapport ou lecture du mois), en secondes. */
  monthlySecondsUsed: number | null;
  /** Dernière lecture de la consommation du mois. */
  usage: VoiceUsage | null;
  usageStatus: VoiceUsageStatus;
  /** Horodatage (ms) de la dernière lecture réussie ou tentée de la consommation. */
  usageFetchedAt: number;
  setLevel: (level: number) => void;
  setState: (state: VoiceState) => void;
  setError: (errorId: string | null, values?: VoiceErrorValues) => void;
  /** Ouverture d'une session réelle : source, limites, compteur remis à zéro. */
  beginRealSession: (info: { source: VoiceSource; limits: VoiceLimits }) => void;
  setSessionSeconds: (seconds: number) => void;
  setMonthlySecondsUsed: (seconds: number) => void;
  setUsage: (usage: VoiceUsage) => void;
  setUsageStatus: (status: VoiceUsageStatus) => void;
  addTranscript: (role: VoiceTranscript['role'], text: string) => void;
  /** Ajoute une carte et rend son identifiant (J2d : la carte de confirmation est mise à jour ensuite). */
  addCard: (card: Omit<VoiceCard, 'id'>) => string;
  /** UPT-2124 J2d : met à jour une carte (état de la confirmation, titre « Dossier créé », lien) ; une carte absente est ignorée. */
  updateCard: (id: string, patch: Partial<Omit<VoiceCard, 'id'>>) => void;
  /** UPT-2124 J2d : retire une carte (« non » / « Annuler » : rien n'est écrit, la carte disparaît). */
  removeCard: (id: string) => void;
  addReport: (report: Omit<VoiceReport, 'id'>) => void;
  setMockActive: (active: boolean) => void;
  reset: () => void;
};

const randomId = (): string => Math.random().toString(36).slice(2, 10);

export const useVoiceStore = create<VoiceStoreState>((set) => ({
  state: 'idle',
  error: null,
  errorId: null,
  errorValues: null,
  transcripts: [],
  cards: [],
  reports: [],
  mockActive: false,
  level: 0,
  realSession: false,
  source: null,
  limits: null,
  sessionSeconds: 0,
  monthlySecondsUsed: null,
  usage: null,
  usageStatus: 'idle',
  usageFetchedAt: 0,
  setLevel: (level) => set({ level }),
  setState: (state) => set({ state }),
  setError: (errorId, values) =>
    set((current) => ({
      errorId,
      errorValues: errorId ? (values ?? null) : null,
      error: errorId,
      state: errorId ? 'error' : current.state,
    })),
  beginRealSession: ({ source, limits }) =>
    set({
      realSession: true,
      source,
      limits,
      sessionSeconds: 0,
      monthlySecondsUsed: limits.monthlySecondsUsed,
    }),
  setSessionSeconds: (sessionSeconds) => set({ sessionSeconds }),
  setMonthlySecondsUsed: (monthlySecondsUsed) => set({ monthlySecondsUsed }),
  setUsage: (usage) =>
    set({
      usage,
      usageStatus: 'ready',
      usageFetchedAt: Date.now(),
      monthlySecondsUsed: usage.seconds.used,
    }),
  setUsageStatus: (usageStatus) =>
    set((current) => ({
      usageStatus,
      usageFetchedAt: usageStatus === 'error' ? Date.now() : current.usageFetchedAt,
    })),
  addTranscript: (role, text) =>
    set((current) => ({
      transcripts: [...current.transcripts, { id: randomId(), role, text, at: new Date().toISOString() }],
    })),
  addCard: (card) => {
    const id = randomId();
    set((current) => ({ cards: [...current.cards, { ...card, id }] }));
    return id;
  },
  updateCard: (id, patch) =>
    set((current) => ({ cards: current.cards.map((card) => (card.id === id ? { ...card, ...patch, id } : card)) })),
  removeCard: (id) => set((current) => ({ cards: current.cards.filter((card) => card.id !== id) })),
  addReport: (report) =>
    set((current) => ({ reports: [...current.reports, { ...report, id: randomId() }] })),
  setMockActive: (mockActive) => set({ mockActive }),
  reset: () =>
    set({
      state: 'idle',
      error: null,
      errorId: null,
      errorValues: null,
      transcripts: [],
      cards: [],
      reports: [],
      mockActive: false,
      level: 0,
      realSession: false,
      source: null,
      limits: null,
      sessionSeconds: 0,
      monthlySecondsUsed: null,
      usage: null,
      usageStatus: 'idle',
      usageFetchedAt: 0,
    }),
}));

/** Sélecteurs simples (hors composants, tests compris). */
export const voiceStore = useVoiceStore;
